'use client';

import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';

import { pickupPointLabel, pickupPointMeta, type PickupPoint } from '@/entities/delivery';
import { pickupPointsQueryOptions } from '@/entities/delivery/client';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogTitle } from '@/shared/ui/dialog';
import { TextButton } from '@/shared/ui/text-link';
import { TextField } from '@/shared/ui/text-field';

import styles from './pickup-point-picker.module.scss';

type Kind = 'all' | 'pvz' | 'postamat';

export type PickupPointPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  city: string;
  onCityChange: (city: string) => void;
  selectedId: string | null;
  onConfirm: (point: PickupPoint) => void;
};

/**
 * Pickup point choice (05B-1). The map is a schematic mock: pins use relative coordinates from the
 * mock API. A real map provider is connected later together with the delivery contract.
 */
export function PickupPointPicker({
  open,
  onOpenChange,
  city,
  onCityChange,
  selectedId,
  onConfirm,
}: PickupPointPickerProps) {
  const deferredCity = useDeferredValue(city);
  const points = useQuery({
    ...pickupPointsQueryOptions(deferredCity),
    enabled: open && deferredCity.trim().length >= 2,
  });
  const [picked, setPicked] = useState<string | null>(selectedId);
  // Every opening starts from the confirmed point: a cancelled pick is discarded (05B "Отмена", ×, backdrop).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setPicked(selectedId);
  }
  const [kind, setKind] = useState<Kind>('all');
  const [search, setSearch] = useState('');
  const [zoom, setZoom] = useState(1);
  const [view, setView] = useState<'map' | 'list'>('map');
  const needle = search.trim().toLocaleLowerCase('ru-RU');
  const list = (points.data ?? []).filter(
    (point) =>
      (kind === 'all' || point.kind === kind) &&
      (!needle || `${point.carrier} ${point.address}`.toLocaleLowerCase('ru-RU').includes(needle)),
  );
  const all = points.data ?? [];
  // A pin is always highlighted: the pick, the confirmed point, the suggested one, or the first.
  const current =
    all.find((point) => point.id === picked) ??
    all.find((point) => point.id === selectedId) ??
    all.find((point) => point.preselected) ??
    all[0] ??
    null;

  return (
    <Dialog
      theme="editorial"
      open={open}
      onOpenChange={onOpenChange}
      title="Выбрать пункт на карте"
      ownTitle
      width="67.5rem"
      backdrop="strong"
      className={styles.dialog}
    >
      <div className={styles.head}>
        <div className={styles.titles}>
          <p className={styles.eyebrow}>Получение · пункт выдачи или постамат</p>
          <DialogTitle className={styles.title}>Выбрать пункт на карте</DialogTitle>
          <span className={styles.caption}>{city || 'Город не указан'} · пункты рядом</span>
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={() => onOpenChange(false)}
          aria-label="Закрыть окно"
        >
          ×
        </button>
      </div>
      <div className={styles.filters}>
        <TextField
          id="pvz-city"
          label="Город"
          value={city}
          onChange={(event) => onCityChange(event.target.value)}
          className={styles.city}
        />
        <TextField
          id="pvz-search"
          label="Поиск"
          type="search"
          placeholder="Улица, метро или район"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <fieldset className={styles.segments} aria-label="Тип пункта">
          {(['all', 'pvz', 'postamat'] as const).map((value) => (
            <label
              key={value}
              className={styles.segment}
              data-checked={kind === value || undefined}
            >
              <input
                type="radio"
                name="pvz-kind"
                className={styles['segment-input']}
                checked={kind === value}
                onChange={() => setKind(value)}
              />
              {value === 'all' ? 'Все' : value === 'pvz' ? 'Пункты' : 'Постаматы'}
            </label>
          ))}
        </fieldset>
      </div>
      <div className={styles.switch} role="tablist" aria-label="Вид">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'map'}
          className={styles.segment}
          onClick={() => setView('map')}
        >
          Карта
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'list'}
          className={styles.segment}
          onClick={() => setView('list')}
        >
          Список
        </button>
      </div>
      <div className={styles.body} data-view={view}>
        <div className={styles.map} aria-label="Схема пунктов выдачи">
          <div className={styles.layer} style={{ '--zoom': zoom }}>
            {list.map((point) => (
              <button
                key={point.id}
                type="button"
                className={styles.pin}
                aria-label={pickupPointLabel(point)}
                aria-pressed={point.id === current?.id}
                style={{ '--x': `${point.x}%`, '--y': `${point.y}%` }}
                onClick={() => setPicked(point.id)}
              />
            ))}
          </div>
          <div className={styles.zoom}>
            <button
              type="button"
              className={styles.close}
              aria-label="Приблизить"
              onClick={() => setZoom((value) => Math.min(1.6, value + 0.3))}
            >
              +
            </button>
            <button
              type="button"
              className={styles.close}
              aria-label="Отдалить"
              onClick={() => setZoom((value) => Math.max(1, value - 0.3))}
            >
              −
            </button>
          </div>
        </div>
        <div className={styles.list}>
          <span className={styles.eyebrow}>Список пунктов</span>
          {points.isPending && open && (
            <output className={styles.caption}>Загружаем пункты…</output>
          )}
          {!points.isPending && list.length === 0 && (
            <output className={styles.caption}>
              {points.isError
                ? 'Не удалось загрузить пункты'
                : 'В этом городе пока нет пунктов выдачи — выберите курьера или Почту России'}
            </output>
          )}
          {list.map((point) => (
            <button
              key={point.id}
              type="button"
              className={styles.point}
              aria-pressed={point.id === current?.id}
              aria-label={`${pickupPointLabel(point)}, ${point.hours} · ${point.distance}`}
              onClick={() => setPicked(point.id)}
            >
              <span>
                <b>{pickupPointLabel(point)}</b>
                <small>{`${point.hours} · ${point.distance}`}</small>
              </span>
              <span className={styles.radio} aria-hidden="true" />
            </button>
          ))}
        </div>
      </div>
      <div className={styles.foot}>
        <div className={styles.selected}>
          <span className={styles.accent}>Выбрано</span>
          <span className={styles.label}>
            {current ? pickupPointLabel(current) : 'Пункт не выбран'}
          </span>
          {current && <span className={styles.caption}>{pickupPointMeta(current)}</span>}
        </div>
        <div className={styles.actions}>
          <TextButton onClick={() => onOpenChange(false)}>Отмена</TextButton>
          <Button
            size="lg"
            aria-disabled={!current || undefined}
            onClick={() => {
              if (current) onConfirm(current);
            }}
          >
            Подтвердить пункт
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
